import { PaperGradientBG } from "../kits";
import rough from "roughjs/bundled/rough.esm.js";
import { useEffect, useLayoutEffect, useState, useRef } from "react";
import { extendedTechStack } from "./TechStack";
import TerminalDetailOverlay from "./TerminalDetailOverlay";
import { TECH_DETAIL_CONFIG, toTechKey } from "./TechStackDetailConfig";

function RoughCircle({ size = 56, color = "#AC8E66", strokeWidth = 1.5, roughness = 1.5, className = "" }) {
  const svgRef = useRef(null);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useLayoutEffect(() => {
    if (!isClient || !svgRef.current) return;
    svgRef.current.innerHTML = "";
    const rc = rough.svg(svgRef.current);
    const padding = strokeWidth;
    const node = rc.circle(size / 2, size / 2, size - padding * 2, {
      roughness,
      stroke: color,
      strokeWidth,
      fill: "none",
    });
    svgRef.current.appendChild(node);
  }, [isClient, size, color, strokeWidth, roughness]);

  if (!isClient) return null;

  return (
    <svg
      ref={svgRef}
      width={size}
      height={size}
      className={`absolute inset-0 pointer-events-none ${className}`}
    />
  );
}

export default function TechStackTerminal() {
  const [activeTechKey, setActiveTechKey] = useState(null);
  const orderedTechKeys = extendedTechStack
    .map((tech) => toTechKey(tech.name))
    .filter((key, index, arr) => arr.indexOf(key) === index && Boolean(TECH_DETAIL_CONFIG[key]));
  const activeTechIndex = activeTechKey ? orderedTechKeys.indexOf(activeTechKey) : -1;
  const activeTechDetail = activeTechKey ? TECH_DETAIL_CONFIG[activeTechKey] : null;

  const openTechDetail = (techName) => {
    const key = toTechKey(techName);
    if (!TECH_DETAIL_CONFIG[key]) return;
    setActiveTechKey(key);
  };
  const handlePrev = () => {
    if (activeTechIndex <= 0) return;
    setActiveTechKey(orderedTechKeys[activeTechIndex - 1]);
  };
  const handleNext = () => {
    if (activeTechIndex < 0 || activeTechIndex >= orderedTechKeys.length - 1) return;
    setActiveTechKey(orderedTechKeys[activeTechIndex + 1]);
  };

  return (
    <PaperGradientBG variant="zen151515">
      <section className="py-12 md:py-20 font-mono">
        <div className="max-w-3xl mx-auto px-6">
          <div className="rounded-lg border border-[#AC8E66]/20 overflow-hidden shadow-2xl shadow-black/40">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-[#1a1a1a] border-b border-[#AC8E66]/10">
              <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
              <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
              <button
                type="button"
                className="w-3 h-3 rounded-full bg-[#28c840] hover:brightness-110 transition"
                onClick={() => setActiveTechKey("react")}
                aria-label="TechStack Detail oeffnen"
              />
              <span className="ml-3 text-xs text-[#AC8E66] select-none">techstack</span>
            </div>
            <div className="px-4 sm:px-6 py-5 bg-[#0F0F0F]">
              <p className="text-sm text-[#EFEBDC]/50 mb-4">
                <span className="text-[#AC8E66]">$</span> stack --list
              </p>
              <div className="flex flex-wrap justify-center gap-4 sm:gap-5">
                {extendedTechStack.map((tech) => (
                  <button
                    key={tech.name}
                    type="button"
                    onClick={() => openTechDetail(tech.name)}
                    className="flex flex-col items-center gap-1.5 cursor-pointer"
                    aria-label={`${tech.name} Details oeffnen`}
                  >
                    <div className="relative w-[60px] h-[60px] sm:w-[68px] sm:h-[68px] flex items-center justify-center bg-[#1c1c1c] rounded-full group border border-transparent hover:border-[#AC8E66]/40 transform-gpu transition-[transform,border-color] duration-200 hover:scale-110">
                      <RoughCircle size={60} color="#AC8E66" strokeWidth={1} roughness={0.8} className="sm:hidden" />
                      <RoughCircle size={68} color="#AC8E66" strokeWidth={1} roughness={0.8} className="hidden sm:block" />
                      <tech.icon
                        className={`w-6 h-6 sm:w-7 sm:h-7 opacity-70 group-hover:opacity-100 transition-opacity ${
                          tech.dark ? "brightness-0 invert" : ""
                        }`}
                        style={{ color: tech.color }}
                      />
                    </div>
                    <span className="text-[10px] font-mono text-[#EFEBDC]/60">{tech.name}</span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-green-400/70 mt-5 pt-4 border-t border-[#AC8E66]/10">
                <span className="text-[#AC8E66]/60">&gt;</span> {extendedTechStack.length}+ stack verifiziert.{" "}
                <button
                  type="button"
                  onClick={() => openTechDetail(extendedTechStack[0]?.name)}
                  className="text-[#AC8E66]/70 hover:text-[#AC8E66] underline underline-offset-2 transition-colors"
                >
                  details anzeigen →
                </button>
              </p>
            </div>
          </div>
        </div>
      </section>
      <TerminalDetailOverlay
        isOpen={Boolean(activeTechDetail)}
        onClose={() => setActiveTechKey(null)}
        title={activeTechDetail?.title || "TechStack"}
        subtitle={activeTechDetail?.subtitle || "ls techstack/"}
        lines={activeTechDetail?.lines || []}
        storageKey={activeTechDetail?.storageKey}
        ctaLabel="Saghallo"
        ctaMode="inlineContact"
        onPrev={handlePrev}
        onNext={handleNext}
        hasPrev={activeTechIndex > 0}
        hasNext={activeTechIndex >= 0 && activeTechIndex < orderedTechKeys.length - 1}
      />
    </PaperGradientBG>
  );
}
